import { motion } from "framer-motion";
import {
  Globe,
  Star,
  MessageSquare,
  Inbox,
  Search,
  CalendarClock,
  Bot,
  Play,
} from "lucide-react";
import { useState } from "react";
import type { Solucion } from "@/lib/soluciones";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CtaSection } from "@/components/CtaSection";
import { SEOHead } from "@/components/SEOHead";

const iconMap = {
  globe: Globe,
  star: Star,
  message: MessageSquare,
  inbox: Inbox,
  search: Search,
  clock: CalendarClock,
  bot: Bot,
};

export const SolucionPage = ({ solucion }: { solucion: Solucion }) => {
  const Icon = iconMap[solucion.icon];
  const [activeVideo, setActiveVideo] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={solucion.seoTitle}
        description={solucion.seoDescription}
        canonical={`/que-hacemos/${solucion.slug}`}
      />
      <Navbar />
      <main className="pt-32 pb-24">
        <div className="container mx-auto px-6 max-w-[1000px]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-2xl mb-16"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-white/5 border border-white/10 rounded-[12px] flex items-center justify-center shrink-0">
                <Icon className="w-6 h-6 text-[#7C3AED]" />
              </div>
              <span className="text-[24px] font-mono font-medium text-transparent bg-clip-text bg-gradient-to-r from-[#7C3AED] to-[#FB7185]">
                {solucion.number}
              </span>
            </div>
            <h1 className="text-[34px] md:text-[48px] font-medium text-[#FAFAFA] tracking-[-0.03em] leading-[1.05] mb-4">
              {solucion.title}
            </h1>
            <p className="text-[#A1A1AA] text-[18px] md:text-[20px] leading-[1.5] italic">
              {solucion.tagline}
            </p>
            <p className="text-[#A1A1AA] text-[16px] md:text-[17px] leading-[1.6] mt-4">
              {solucion.description}
            </p>
          </motion.div>

          {/* Video */}
          {solucion.videoUrl ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.6,
                delay: 0.1,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="w-full aspect-video bg-elevated border border-white/10 rounded-2xl relative overflow-hidden group mb-20"
            >
              {activeVideo ? (
                <video
                  src={solucion.videoUrl}
                  poster={solucion.thumbnailUrl}
                  controls
                  autoPlay
                  playsInline
                  className="w-full h-full object-contain bg-black"
                />
              ) : (
                <button
                  type="button"
                  className="absolute inset-0 flex items-center justify-center z-10 group-hover:bg-transparent transition-colors"
                  onClick={() => setActiveVideo(true)}
                >
                  {solucion.thumbnailUrl ? (
                    <img
                      src={solucion.thumbnailUrl}
                      alt={solucion.title}
                      className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-95 group-hover:scale-[1.02] transition-all duration-500"
                    />
                  ) : null}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                  <div className="w-16 h-16 rounded-full bg-[#08080A]/90 border border-white/20 flex items-center justify-center group-hover:scale-110 group-hover:border-primary/60 transition-all duration-300 z-10 shadow-2xl backdrop-blur-sm">
                    <Play className="w-6 h-6 text-primary ml-1" />
                  </div>
                </button>
              )}
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.6,
                delay: 0.1,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="w-full aspect-video bg-elevated border border-white/10 rounded-2xl flex items-center justify-center mb-20"
            >
              <p className="text-[#71717A] font-mono text-sm">Próximamente</p>
            </motion.div>
          )}

          {/* Bullets */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="bg-[#0F0F11] border border-white/5 rounded-[16px] p-8 lg:p-10"
            style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)" }}
          >
            <h2 className="text-[#FAFAFA] text-[24px] lg:text-[28px] font-medium tracking-[-0.03em] mb-8">
              Qué incluye
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              {solucion.bullets.map((bullet, bidx) => (
                <div key={bidx}>
                  <p className="text-[#FAFAFA] text-[15px] font-medium mb-1">
                    {bullet.label}
                  </p>
                  <p className="text-[#A1A1AA] text-[14px] leading-[1.5]">
                    {bullet.text}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </main>
      <CtaSection />
      <Footer />
    </div>
  );
};
